import React, { useCallback, useEffect, useState } from "react";

import { faExternalLinkAlt } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Form, { FormProps } from "antd/es/form";
import { FieldData } from "rc-field-form/lib/interface";

import { addSchemeToUrlIfEmpty } from "@mapmycustomers/shared/util/url";

import Button from "../../../Button";
import { useConfigProvider } from "../../../ConfigProvider";
import TextField from "../../../input/TextField";
import Modal from "../../../Modal";

import styles from "./LinkModal.module.scss";

interface FormValues {
  url: string;
}

interface LinkModalProps {
  initialUrl?: string;
  onCancel: () => void;
  onSave: (url: string) => void;
}

const LinkModal: React.FC<LinkModalProps> = ({ initialUrl, onCancel, onSave }) => {
  const configProvider = useConfigProvider();
  const [form] = Form.useForm<FormValues>();

  const [urlInvalid, setUrlInvalid] = useState(false);

  const url = Form.useWatch("url", form);

  useEffect(() => {
    setUrlInvalid(form.getFieldError("url").length > 0);
  }, [form, url]);

  const handlePreviewClick = useCallback(() => {
    window.open(addSchemeToUrlIfEmpty(url), "_blank");
  }, [url]);

  const handleOkClick = useCallback(() => {
    form.submit();
  }, [form]);

  const handleFormFinish: FormProps<FormValues>["onFinish"] = useCallback(
    (values) => {
      onSave(addSchemeToUrlIfEmpty(values.url));
    },
    [onSave]
  );

  const handleFormFieldsChange: FormProps<FormValues>["onFieldsChange"] = useCallback(
    (changedFields: FieldData[]) => {
      const urlField = changedFields.find((field) => (field.name as string[])[0] === "url");
      if (urlField) {
        setUrlInvalid(!!urlField.errors?.length);
      }
    },
    []
  );

  return (
    <Modal
      cancelButtonProps={{ hidden: true }}
      okText={configProvider.formatMessage("ui.emailBody.imageModal.ok")}
      onCancel={onCancel}
      onOk={handleOkClick}
      open
      title={configProvider.formatMessage("ui.emailBody.imageModal.linkTitle")}
    >
      <Form
        form={form}
        initialValues={{ url: initialUrl }}
        layout="vertical"
        onFieldsChange={handleFormFieldsChange}
        onFinish={handleFormFinish}
      >
        <div className={styles.urlContainer}>
          <Form.Item
            label={configProvider.formatMessage("ui.emailBody.imageModal.url")}
            name="url"
            required
            rules={[
              {
                message: configProvider.formatMessage("ui.emailBody.validation.urlRequired"),
                required: true,
              },
              {
                message: configProvider.formatMessage("ui.emailBody.validation.urlInvalid"),
                type: "url",
              },
            ]}
          >
            <TextField />
          </Form.Item>
          <Button
            className={styles.previewButton}
            disabled={!url || urlInvalid}
            onClick={handlePreviewClick}
            rightIcon={<FontAwesomeIcon icon={faExternalLinkAlt} />}
            type="link"
          >
            {configProvider.formatMessage("ui.emailBody.imageModal.preview")}
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default LinkModal;
